import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import ConfirmationModal from "../../components/Assets/ConfirmationModal";
import Loading from "../../components/AssetsComponent/loading";
import { RootDispatch, RootState } from "../../store";
import {
  RefurbishedStockHistoryItem,
  RefurbishedStockQuery,
  RefurbishedStockRecord,
  StockReference,
} from "../../api/refurbishedStock.api";
import {
  clearRefurbishedStockError,
  fetchRefurbishedStock,
  markStockRefurbished,
} from "../../store/slices/refurbishedStockSlice";
import PaginationControls from "../../utils/PaginationControls";
import {
  canMarkRefurbished,
  formatStockDate,
  getStockReferenceLabel,
  sortReplacementStockHistory,
} from "./refurbishedStock.utils";

interface RefurbishedStockFilters {
  status: "" | "needs_repair" | "refurbished";
  warehouseId: string;
  productId: string;
  dispatched: "" | "true" | "false";
  receivedFrom: string;
  receivedTo: string;
}

const initialFilters: RefurbishedStockFilters = {
  status: "",
  warehouseId: "",
  productId: "",
  dispatched: "",
  receivedFrom: "",
  receivedTo: "",
};

const displayLookup = (
  reference: StockReference | undefined,
  lookups: { _id: string; label: string }[],
) => {
  if (!reference) return "—";
  if (typeof reference !== "string") {
    const label = getStockReferenceLabel(reference);
    if (label) return label;
    const match = lookups.find((item) => item._id === reference._id);
    return match?.label || "—";
  }

  const match = lookups.find((item) => item._id === reference);
  if (match) return match.label;
  return /^[a-f\d]{24}$/i.test(reference) ? "—" : reference;
};

const RefurbishedStockDetailsModal = ({
  record,
  onClose,
  warehouses,
}: {
  record: RefurbishedStockRecord | null;
  onClose: () => void;
  warehouses: { _id: string; label: string }[];
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const history = useMemo(
    () => sortReplacementStockHistory(record?.history),
    [record?.history],
  );

  useEffect(() => {
    if (record) {
      previousFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      closeButtonRef.current?.focus();
    } else {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }
  }, [record]);

  if (!record) return null;

  const renderHistoryItem = (
    item: RefurbishedStockHistoryItem,
    index: number,
  ) => {
    const priorStatus =
      item.previousStatus || item.oldStatus || item.fromStatus;
    const nextStatus = item.newStatus || item.toStatus || item.status;
    const statusChange = [priorStatus, nextStatus].filter(Boolean).join(" -> ");
    const actor =
      item.actor ||
      item.performedBy ||
      item.user ||
      item.userId ||
      item.createdBy;
    const warehouse = item.warehouse || item.warehouseId;
    const relatedSerial =
      item.relatedSerialNumber ||
      item.relatedSerial ||
      item.serialNumber ||
      item.replacementSerialNumber;
    const order =
      item.relatedOrder ||
      item.relatedOrderId ||
      item.order ||
      item.orderId ||
      item.orderNumber;

    return (
      <li
        key={`${item.date || item.createdAt || item.timestamp || "history"}-${index}`}
        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800">
              {item.action || "Stock update"}
            </h3>
            {statusChange && (
              <p className="mt-1 text-sm text-slate-600">
                Status: {statusChange}
              </p>
            )}
          </div>
          <time className="shrink-0 text-xs text-slate-500">
            {formatStockDate(
              item.date ||
                item.createdAt ||
                item.timestamp ||
                item.performedAt,
              true,
            )}
          </time>
        </div>
        <dl className="mt-3 grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          {actor && (
            <div>
              <dt className="text-xs font-medium text-slate-500">Actor</dt>
              <dd className="text-slate-700">
                {getStockReferenceLabel(actor) || "—"}
              </dd>
            </div>
          )}
          {warehouse && (
            <div>
              <dt className="text-xs font-medium text-slate-500">Warehouse</dt>
              <dd className="text-slate-700">
                {displayLookup(warehouse, warehouses)}
              </dd>
            </div>
          )}
          {relatedSerial && (
            <div>
              <dt className="text-xs font-medium text-slate-500">
                Related serial
              </dt>
              <dd className="break-all text-slate-700">{relatedSerial}</dd>
            </div>
          )}
          {order && (
            <div>
              <dt className="text-xs font-medium text-slate-500">Order</dt>
              <dd className="text-slate-700">
                {getStockReferenceLabel(order) || "—"}
              </dd>
            </div>
          )}
          {item.reason && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-slate-500">Reason</dt>
              <dd className="whitespace-pre-wrap text-slate-700">
                {item.reason}
              </dd>
            </div>
          )}
        </dl>
      </li>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="refurbished-stock-history-title"
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
          if (event.key === "Tab") {
            const focusableElements =
              dialogRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
              );
            if (!focusableElements?.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            } else if (
              !event.shiftKey &&
              document.activeElement === lastElement
            ) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }}
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-slate-50 shadow-2xl outline-none"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2
              id="refurbished-stock-history-title"
              className="text-lg font-semibold text-slate-900"
            >
              Replacement stock history
            </h2>
            <p className="mt-1 break-all text-sm text-slate-600">
              Serial number: {record.serialNumber}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close replacement stock history"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </header>
        <div className="overflow-y-auto p-4 sm:p-6">
          {history.length ? (
            <ol className="space-y-3">
              {history.map(renderHistoryItem)}
            </ol>
          ) : (
            <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              No replacement history is available for this record.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const RefurbishedStockPage = () => {
  const dispatch = useDispatch<RootDispatch>();
  const warehouses = useSelector(
    (state: RootState) => state.warehouse.warehouseNamesAndIds || [],
  );
  const products = useSelector(
    (state: RootState) => state.product.products || [],
  );
  const {
    items,
    pagination,
    listLoading,
    listError,
    repairingSerialNumber,
    repairError,
  } = useSelector((state: RootState) => state.refurbishedStock);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] =
    useState<RefurbishedStockFilters>(initialFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [expandedRecordIds, setExpandedRecordIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [detailsRecord, setDetailsRecord] =
    useState<RefurbishedStockRecord | null>(null);
  const [repairRecord, setRepairRecord] =
    useState<RefurbishedStockRecord | null>(null);

  const warehouseLookups = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        _id: warehouse._id,
        label: warehouse.name || "",
      })),
    [warehouses],
  );
  const productLookups = useMemo(
    () =>
      products.map((product) => ({
        _id: product._id,
        label: product.productName || "",
      })),
    [products],
  );

  const query = useMemo<RefurbishedStockQuery>(
    () => ({
      page,
      limit: 10,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.warehouseId ? { warehouseId: filters.warehouseId } : {}),
      ...(filters.productId ? { productId: filters.productId } : {}),
      ...(filters.dispatched ? { dispatched: filters.dispatched } : {}),
      ...(filters.receivedFrom ? { receivedFrom: filters.receivedFrom } : {}),
      ...(filters.receivedTo ? { receivedTo: filters.receivedTo } : {}),
    }),
    [page, debouncedSearch, filters],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchRefurbishedStock(query));
  }, [dispatch, query]);

  const updateFilter = <K extends keyof RefurbishedStockFilters>(
    key: K,
    value: RefurbishedStockFilters[K],
  ) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setFilters(initialFilters);
    setPage(1);
    setFiltersExpanded(false);
  };

  const hasFilters =
    Boolean(searchInput || debouncedSearch) ||
    Object.values(filters).some(Boolean);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const toggleRecordDetails = (recordId: string) => {
    setExpandedRecordIds((current) => {
      const next = new Set(current);
      if (next.has(recordId)) next.delete(recordId);
      else next.add(recordId);
      return next;
    });
  };

  const renderExpandedDetails = (record: RefurbishedStockRecord) => (
    <dl className="grid gap-x-5 gap-y-3 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <dt className="text-xs font-medium text-slate-500">
          Replacement serial
        </dt>
        <dd className="mt-1 break-all text-slate-800">
          {record.replacementSerialNumber || "—"}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-slate-500">Received date</dt>
        <dd className="mt-1 text-slate-800">
          {formatStockDate(record.receivedDate)}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-slate-500">Repaired date</dt>
        <dd className="mt-1 text-slate-800">
          {formatStockDate(
            record.refurbishedDate ||
              record.repairedDate ||
              record.repairedAt,
          )}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-slate-500">Repaired by</dt>
        <dd className="mt-1 break-words text-slate-800">
          {getStockReferenceLabel(
            record.refurbishedBy || record.repairedBy,
          ) || "—"}
        </dd>
      </div>
      <div className="sm:col-span-2 lg:col-span-3">
        <dt className="text-xs font-medium text-slate-500">Reason</dt>
        <dd className="mt-1 whitespace-pre-wrap break-words text-slate-800">
          {record.reason || "—"}
        </dd>
      </div>
    </dl>
  );

  const renderRecordActions = (
    record: RefurbishedStockRecord,
    layout: "mobile" | "desktop",
  ) => {
    const expanded = expandedRecordIds.has(record._id);
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={`refurbished-record-${layout}-${record._id}`}
          onClick={() => toggleRecordDetails(record._id)}
          className="min-h-10 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className="inline-flex items-center gap-1">
            {expanded ? "Less" : "More"}
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setDetailsRecord(record)}
          aria-label={`View history for serial ${record.serialNumber}`}
          className="min-h-10 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          History
        </button>
        {canMarkRefurbished(record) && (
          <button
            type="button"
            disabled={listLoading}
            onClick={() => {
              dispatch(clearRefurbishedStockError());
              setRepairRecord(record);
            }}
            className="min-h-10 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Mark Refurbished
          </button>
        )}
      </div>
    );
  };

  const retryFetch = () => {
    dispatch(fetchRefurbishedStock(query));
  };

  const confirmMarkRefurbished = async () => {
    if (!repairRecord || !canMarkRefurbished(repairRecord)) return;
    try {
      await dispatch(
        markStockRefurbished(repairRecord.serialNumber),
      ).unwrap();
      toast.success("Stock marked as refurbished.");
      setRepairRecord(null);
      dispatch(fetchRefurbishedStock(query));
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to mark stock as refurbished.",
      );
    }
  };

  return (
    <section className="space-y-5" aria-labelledby="refurbished-stock-heading">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            id="refurbished-stock-heading"
            className="text-lg font-semibold text-slate-900"
          >
            Refurbished Stock
          </h2>
          <p className="text-sm text-slate-500">
            Track returned units and their repair status.
          </p>
        </div>
        <p className="text-sm text-slate-500" aria-live="polite">
          {pagination.totalCount} record{pagination.totalCount === 1 ? "" : "s"}
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1">
            <label htmlFor="refurbished-stock-search" className="sr-only">
              Search serial number, replacement serial, or reason
            </label>
            <input
              id="refurbished-stock-search"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search refurbished stock..."
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              aria-expanded={filtersExpanded}
              aria-controls="refurbished-stock-filter-panel"
              onClick={() => setFiltersExpanded((expanded) => !expanded)}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:flex-none"
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                  {activeFilterCount}
                </span>
              )}
              {filtersExpanded ? (
                <ChevronUp size={16} aria-hidden="true" />
              ) : (
                <ChevronDown size={16} aria-hidden="true" />
              )}
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="min-h-11 flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:flex-none"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div
          id="refurbished-stock-filter-panel"
          className={`${filtersExpanded ? "grid" : "hidden"} gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 sm:p-4 xl:grid-cols-3`}
        >
          <div>
            <label
              htmlFor="refurbished-stock-status"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Status
            </label>
            <select
              id="refurbished-stock-status"
              value={filters.status}
              onChange={(event) =>
                updateFilter(
                  "status",
                  event.target.value as RefurbishedStockFilters["status"],
                )
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="needs_repair">Needs repair</option>
              <option value="refurbished">Refurbished</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="refurbished-stock-warehouse"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Warehouse
            </label>
            <select
              id="refurbished-stock-warehouse"
              value={filters.warehouseId}
              onChange={(event) =>
                updateFilter("warehouseId", event.target.value)
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All warehouses</option>
              {warehouseLookups.map((warehouse) => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="refurbished-stock-product"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Product
            </label>
            <select
              id="refurbished-stock-product"
              value={filters.productId}
              onChange={(event) =>
                updateFilter("productId", event.target.value)
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All products</option>
              {productLookups.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="refurbished-stock-dispatched"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Dispatch status
            </label>
            <select
              id="refurbished-stock-dispatched"
              value={filters.dispatched}
              onChange={(event) =>
                updateFilter(
                  "dispatched",
                  event.target.value as RefurbishedStockFilters["dispatched"],
                )
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">All dispatch statuses</option>
              <option value="false">Not dispatched</option>
              <option value="true">Dispatched</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="refurbished-received-from"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Received from
            </label>
            <input
              id="refurbished-received-from"
              type="date"
              value={filters.receivedFrom}
              onChange={(event) =>
                updateFilter("receivedFrom", event.target.value)
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="refurbished-received-to"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Received to
            </label>
            <input
              id="refurbished-received-to"
              type="date"
              value={filters.receivedTo}
              onChange={(event) =>
                updateFilter("receivedTo", event.target.value)
              }
              className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {listError && (
        <div
          role="alert"
          className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between"
        >
          <span>{listError}</span>
          <button
            type="button"
            onClick={retryFetch}
            className="self-start font-semibold underline sm:self-auto"
          >
            Retry
          </button>
        </div>
      )}

      {listLoading && items.length === 0 ? (
        <Loading />
      ) : items.length === 0 && !listError ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-sm text-slate-500">
          No refurbished stock records match these filters.
        </div>
      ) : (
        items.length > 0 && (
          <>
            <div className="flex items-center justify-end text-xs text-slate-500" aria-live="polite">
              {listLoading && <span>Refreshing stock records...</span>}
            </div>
            <div className="space-y-3 lg:hidden">
              {items.map((record) => {
                const expanded = expandedRecordIds.has(record._id);
                const productReference = record.product || record.productId;
                const warehouseReference =
                  record.warehouse || record.warehouseId;
                return (
                  <article
                    key={record._id}
                    className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Serial number
                        </p>
                        <p className="mt-1 break-all text-sm font-semibold text-slate-900">
                          {record.serialNumber}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          record.status === "refurbished"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {record.status === "refurbished"
                          ? "Refurbished"
                          : "Needs repair"}
                      </span>
                    </div>

                    <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-sm">
                      <div className="min-w-0">
                        <dt className="text-xs text-slate-500">Product</dt>
                        <dd className="mt-0.5 break-words font-medium text-slate-800">
                          {displayLookup(productReference, productLookups)}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs text-slate-500">Warehouse</dt>
                        <dd className="mt-0.5 break-words font-medium text-slate-800">
                          {displayLookup(warehouseReference, warehouseLookups)}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-xs text-slate-500">Dispatch</dt>
                        <dd className="mt-0.5 font-medium text-slate-800">
                          {record.dispatchedDate
                            ? `Dispatched ${formatStockDate(record.dispatchedDate)}`
                            : "Not dispatched"}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-3 border-t border-slate-100 pt-3">
                      {renderRecordActions(record, "mobile")}
                    </div>
                    <div
                      id={`refurbished-record-mobile-${record._id}`}
                      hidden={!expanded}
                      className="mt-3 border-t border-slate-100 pt-3"
                    >
                      {renderExpandedDetails(record)}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white lg:block">
              <table className="w-full min-w-[880px] divide-y divide-slate-200 text-left">
                <thead className="bg-slate-50">
                  <tr>
                    {[
                      "Serial number",
                      "Product",
                      "Warehouse",
                      "Status",
                      "Dispatch",
                      "Actions",
                    ].map((heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((record) => {
                    const expanded = expandedRecordIds.has(record._id);
                    const productReference = record.product || record.productId;
                    const warehouseReference =
                      record.warehouse || record.warehouseId;
                    return (
                      <Fragment key={record._id}>
                        <tr
                          className="align-middle hover:bg-slate-50"
                        >
                          <td className="max-w-48 break-all px-3 py-3 text-sm font-medium text-slate-900">
                            {record.serialNumber}
                          </td>
                          <td className="max-w-48 px-3 py-3 text-sm text-slate-700">
                            {displayLookup(productReference, productLookups)}
                          </td>
                          <td className="max-w-40 px-3 py-3 text-sm text-slate-700">
                            {displayLookup(warehouseReference, warehouseLookups)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm">
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                record.status === "refurbished"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-amber-100 text-amber-800"
                              }`}
                            >
                              {record.status === "refurbished"
                                ? "Refurbished"
                                : "Needs repair"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-slate-700">
                            {record.dispatchedDate ? (
                              <span>
                                Dispatched{" "}
                                <span className="text-xs text-slate-500">
                                  {formatStockDate(record.dispatchedDate)}
                                </span>
                              </span>
                            ) : (
                              "Not dispatched"
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {renderRecordActions(record, "desktop")}
                          </td>
                        </tr>
                        <tr
                          id={`refurbished-record-desktop-${record._id}`}
                          key={`${record._id}-expanded`}
                          hidden={!expanded}
                        >
                          <td colSpan={6} className="px-4 pb-4">
                            {renderExpandedDetails(record)}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination.totalPages > 1 && (
              <PaginationControls
                currentPage={pagination.page || page}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalCount}
                isLoading={listLoading}
                onPageChange={(nextPage) =>
                  setPage(
                    Math.min(
                      pagination.totalPages,
                      Math.max(1, nextPage),
                    ),
                  )
                }
              />
            )}
          </>
        )
      )}

      <RefurbishedStockDetailsModal
        record={detailsRecord}
        onClose={() => setDetailsRecord(null)}
        warehouses={warehouseLookups}
      />
      <ConfirmationModal
        isOpen={Boolean(repairRecord)}
        title="Confirm repair completion"
        subTitle={
          repairRecord
            ? `Mark ${repairRecord.serialNumber} as refurbished?`
            : ""
        }
        description={
          repairError ||
          "This records the repair as complete. The unit must not have been dispatched."
        }
        confirmText="Mark Refurbished"
        cancelText="Cancel"
        danger={false}
        loading={repairingSerialNumber === repairRecord?.serialNumber}
        onClose={() => {
          if (!repairingSerialNumber) setRepairRecord(null);
        }}
        onConfirm={confirmMarkRefurbished}
      />
    </section>
  );
};

export default RefurbishedStockPage;
